import React, { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import Image from "react-bootstrap/Image";
import Alert from "react-bootstrap/Alert";
import ProgressBar from "react-bootstrap/ProgressBar";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import InputGroup from "react-bootstrap/InputGroup";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import Select from "react-select";

const sizeOptions = [
  { value: "XS", label: "XS" },
  { value: "S", label: "S" },
  { value: "M", label: "M" },
  { value: "L", label: "L" },
  { value: "XL", label: "XL" },
  { value: "XXL", label: "XXL" },
  { value: "XXXL", label: "XXXL" },
  ...Array.from({ length: 18 }, (_, i) => {
    const size = (28 + i).toString();
    return { value: size, label: size };
  }),
];

const supabaseUrl = process.env.REACT_APP_SUPA_URL;
const supabaseKey = process.env.REACT_APP_SUPA_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

const ADMIN_CREDENTIALS = {
  username: process.env.REACT_APP_ADMIN_USER,
  password: process.env.REACT_APP_ADMIN_PASS,
};

// Login Component
const AdminLogin = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    setTimeout(() => {
      if (
        username === ADMIN_CREDENTIALS.username &&
        password === ADMIN_CREDENTIALS.password
      ) {
        // Store auth token in memory (you could use sessionStorage for persistence)
        const authToken = btoa(`${username}:${Date.now()}`);
        onLoginSuccess(authToken);
      } else {
        setError("Invalid username or password");
        setPassword("");
      }
      setLoading(false);
    }, 500);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col md={5} lg={4}>
            <Card className="shadow-lg border-0 rounded-4">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <div
                    className="mx-auto mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ fontSize: "40px", color: "white" }}>🔐</span>
                  </div>
                  <h3 className="fw-bold mb-1">Admin Login</h3>
                  <p className="text-muted">
                    Enter your credentials to continue
                  </p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Username</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>👤</InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Enter username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        disabled={loading}
                        autoFocus
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium">Password</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>🔑</InputGroup.Text>
                      <Form.Control
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-2"
                    disabled={loading || !username || !password}
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </Button>
                </Form>

                <div className="text-center mt-4">
                  <small className="text-muted">
                    This section of the website is restricted to authorized
                    personnel. Unauthorized access attempts are strictly
                    prohibited and may be logged, monitored, and reported.
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showCompletedOrders, setShowCompletedOrders] = useState(false);
  const [newProduct, setNewProduct] = useState({
    title: "",
    price: "",
    description: "",
    category: "",
    size: "",
    quantity: 0,
  });

  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductData, setEditProductData] = useState({
    title: "",
    price: "",
    description: "",
    category: "",
    size: "",
    images: [],
    quantity: 0,
    isHidden: false,
  });
  const [editNewFiles, setEditNewFiles] = useState([]);
  const [editImagePreviews, setEditImagePreviews] = useState([]);
  const [editUploadProgress, setEditUploadProgress] = useState({});
  const [updatingProduct, setUpdatingProduct] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [updatingOrderStatus, setUpdatingOrderStatus] = useState(false);

  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [orderSearchTerm, setOrderSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Check for existing auth on mount
  useEffect(() => {
    // You could check sessionStorage here if you want persistent login
    // const savedToken = sessionStorage.getItem('adminAuthToken');
    // if (savedToken) {
    //   setAuthToken(savedToken);
    //   setIsAuthenticated(true);
    // }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
      fetchOrders();
    }
  }, [isAuthenticated]);

  const handleLoginSuccess = (token) => {
    setAuthToken(token);
    setIsAuthenticated(true);
    // Optionally store in sessionStorage for persistence
    // sessionStorage.setItem('adminAuthToken', token);
    showAlert("Login successful! Welcome to Admin Dashboard", "success");
  };

  const handleLogout = () => {
    setAuthToken(null);
    setIsAuthenticated(false);
    // sessionStorage.removeItem('adminAuthToken');
    showAlert("Logged out successfully", "info");
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}product/getAllProducts`
      );
      const productsData = res.data.products || res.data.data || res.data || [];
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error("Error fetching products:", error);
      showAlert("Error fetching products: " + error.message, "danger");
      setProducts([]);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}order/getAllOrders`
      );
      const ordersData = res.data.orders || res.data.data || res.data || [];
      setOrders(Array.isArray(ordersData) ? ordersData : []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      showAlert("Error fetching orders: " + error.message, "danger");
      setOrders([]);
    }
  };

  const getFilteredOrders = () => {
    if (showCompletedOrders) {
      return orders.filter(
        (order) =>
          order.status?.toLowerCase() === "delivered" ||
          order.status?.toLowerCase() === "canceled" ||
          order.status?.toLowerCase() === "cancelled"
      );
    } else {
      return orders.filter(
        (order) =>
          order.status?.toLowerCase() !== "delivered" &&
          order.status?.toLowerCase() !== "canceled" &&
          order.status?.toLowerCase() !== "cancelled"
      );
    }
  };

  const getOrderCounts = () => {
    const activeOrders = orders.filter(
      (order) =>
        order.status?.toLowerCase() !== "delivered" &&
        order.status?.toLowerCase() !== "canceled" &&
        order.status?.toLowerCase() !== "cancelled"
    );
    const completedOrders = orders.filter(
      (order) =>
        order.status?.toLowerCase() === "delivered" ||
        order.status?.toLowerCase() === "canceled" ||
        order.status?.toLowerCase() === "cancelled"
    );
    return {
      active: activeOrders.length,
      completed: completedOrders.length,
      total: orders.length,
    };
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setIsSelectAllChecked(checked);
    if (checked) {
      setSelectedProducts(getFilteredProducts().map((p) => p._id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productId)) {
        const newSelection = prev.filter((id) => id !== productId);
        setIsSelectAllChecked(false);
        return newSelection;
      } else {
        const newSelection = [...prev, productId];
        const filteredProducts = getFilteredProducts();
        setIsSelectAllChecked(newSelection.length === filteredProducts.length);
        return newSelection;
      }
    });
  };

  const handleMassDelete = async () => {
    if (selectedProducts.length === 0) {
      showAlert("Please select at least one product", "warning");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedProducts.length} product(s)?`
      )
    ) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const productId of selectedProducts) {
      try {
        const product = products.find((p) => p._id === productId);
        await axios.delete(
          `${process.env.REACT_APP_API_BASE}product/deletebyID?id=${productId}`
        );

        if (
          supabase &&
          product &&
          product.images &&
          Array.isArray(product.images)
        ) {
          for (const imageUrl of product.images) {
            try {
              const urlParts = imageUrl.split("/");
              const fileName = urlParts[urlParts.length - 1];
              const filePath = `products/${fileName}`;
              await supabase.storage.from("blinq").remove([filePath]);
            } catch (imgError) {
              console.warn("Failed to delete image from storage:", imgError);
            }
          }
        }
        successCount++;
      } catch (error) {
        console.error(`Error deleting product ${productId}:`, error);
        failCount++;
      }
    }

    setLoading(false);
    setSelectedProducts([]);
    setIsSelectAllChecked(false);
    await fetchProducts();

    if (failCount === 0) {
      showAlert(`Successfully deleted ${successCount} product(s)!`, "success");
    } else {
      showAlert(
        `Deleted ${successCount} product(s), failed to delete ${failCount} product(s)`,
        "warning"
      );
    }
  };

  const handleMassHide = async (shouldHide) => {
    if (selectedProducts.length === 0) {
      showAlert("Please select at least one product", "warning");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to ${shouldHide ? "hide" : "unhide"} ${
          selectedProducts.length
        } product(s)?`
      )
    ) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const productId of selectedProducts) {
      try {
        const product = products.find((p) => p._id === productId);
        if (!product) continue;

        const updatedData = {
          Title: product.title || product.Title,
          price: product.price,
          Description: product.description || product.Description,
          Category: product.category || product.Category,
          size: product.size || "",
          image_link: product.image_link || product.images || [],
          quantity: product.quantity || 0,
          isHidden: shouldHide,
        };

        await axios.put(
          `${process.env.REACT_APP_API_BASE}product/edit?id=${productId}`,
          updatedData
        );
        successCount++;
      } catch (error) {
        console.error(`Error updating product ${productId}:`, error);
        failCount++;
      }
    }

    setLoading(false);
    setSelectedProducts([]);
    setIsSelectAllChecked(false);
    await fetchProducts();

    if (failCount === 0) {
      showAlert(
        `Successfully ${
          shouldHide ? "hidden" : "unhidden"
        } ${successCount} product(s)!`,
        "success"
      );
    } else {
      showAlert(
        `Updated ${successCount} product(s), failed to update ${failCount} product(s)`,
        "warning"
      );
    }
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm.trim()) return products;

    const searchLower = productSearchTerm.toLowerCase();
    return products.filter((product) => {
      const title = (product.title || product.Title || "").toLowerCase();
      const category = (
        product.category ||
        product.Category ||
        ""
      ).toLowerCase();
      const description = (
        product.description ||
        product.Description ||
        ""
      ).toLowerCase();
      const price = (product.price || "").toString();

      return (
        title.includes(searchLower) ||
        category.includes(searchLower) ||
        description.includes(searchLower) ||
        price.includes(searchLower)
      );
    });
  };

  const getFilteredOrdersList = () => {
    const baseFiltered = getFilteredOrders();

    if (!orderSearchTerm.trim()) return baseFiltered;

    const searchLower = orderSearchTerm.toLowerCase();
    return baseFiltered.filter((order) => {
      const orderId = (order._id || "").toLowerCase();
      const customerName = (
        order.shippingAddress?.fullName ||
        order.shippingAddress?.name ||
        ""
      ).toLowerCase();
      const phone = (order.shippingAddress?.phone || "").toLowerCase();
      const city = (order.shippingAddress?.city || "").toLowerCase();
      const address = (order.shippingAddress?.address || "").toLowerCase();
      const status = (order.status || "").toLowerCase();

      return (
        orderId.includes(searchLower) ||
        customerName.includes(searchLower) ||
        phone.includes(searchLower) ||
        city.includes(searchLower) ||
        address.includes(searchLower) ||
        status.includes(searchLower)
      );
    });
  };

  const cancelOrder = async (orderId, event = null) => {
    if (event) event.stopPropagation();
    if (!orderId) {
      showAlert("Invalid order ID", "danger");
      return;
    }
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    setUpdatingOrderStatus(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}order/cancelOrder?id=${orderId}`
      );
      if (
        response.data &&
        (response.data.message === "Order cancelled successfully" ||
          response.data.success)
      ) {
        const updatedOrder = response.data.order || { status: "canceled" };
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === orderId
              ? { ...order, status: "canceled", ...updatedOrder }
              : order
          )
        );
        if (selectedOrder && selectedOrder._id === orderId) {
          setSelectedOrder((prev) => ({
            ...prev,
            status: "canceled",
            ...updatedOrder,
          }));
        }
        showAlert("Order cancelled successfully", "success");
        if (
          showOrderDetails &&
          selectedOrder &&
          selectedOrder._id === orderId
        ) {
          setShowOrderDetails(false);
        }
      } else {
        throw new Error(response.data?.message || "Failed to cancel order");
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      showAlert("Error cancelling order: " + error.message, "danger");
    } finally {
      setUpdatingOrderStatus(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus, event = null) => {
    if (event) event.stopPropagation();
    if (!orderId || !newStatus) {
      showAlert("Invalid order ID or status", "danger");
      return;
    }
    setUpdatingOrderStatus(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}order/updateOrder?id=${orderId}`
      );
      if (
        response.data &&
        response.data.message === "Order status updated successfully" &&
        response.data.order
      ) {
        const updatedOrder = response.data.order;
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === orderId ? { ...order, ...updatedOrder } : order
          )
        );
        if (selectedOrder && selectedOrder._id === orderId) {
          setSelectedOrder((prev) => ({ ...prev, ...updatedOrder }));
        }
        showAlert(`Order status updated to ${updatedOrder.status}`, "success");
      } else {
        throw new Error(response.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      showAlert("Error updating order status: " + error.message, "danger");
    } finally {
      setUpdatingOrderStatus(false);
    }
  };

  const getStatusButtonVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "processing":
        return "warning";
      case "shipped":
        return "info";
      case "delivered":
        return "success";
      case "canceled":
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getNextStatusInfo = (currentStatus) => {
    switch (currentStatus?.toLowerCase()) {
      case "processing":
        return { nextStatus: "shipped", buttonText: "Mark as Shipped" };
      case "shipped":
        return { nextStatus: "delivered", buttonText: "Mark as Delivered" };
      case "delivered":
        return { nextStatus: null, buttonText: "Completed" };
      case "canceled":
      case "cancelled":
        return { nextStatus: null, buttonText: "Canceled" };
      default:
        return { nextStatus: "shipped", buttonText: "Mark as Shipped" };
    }
  };

  const handleOrderClick = async (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
    setLoadingOrderDetails(true);
    try {
      if (order.items && order.items.length > 0) {
        const itemsWithDetails = await Promise.all(
          order.items.map(async (item) => {
            try {
              const productId = item.product || item.productId;
              if (productId) {
                const response = await axios.get(
                  `${process.env.REACT_APP_API_BASE}product/getProductById?id=${productId}`
                );
                const productDetails = response.data.prod;
                if (productDetails && productDetails._id) {
                  return {
                    ...item,
                    title:
                      productDetails.Title ||
                      productDetails.title ||
                      `Product ${productId}`,
                    description:
                      productDetails.Description ||
                      productDetails.description ||
                      "No description available",
                    category:
                      productDetails.Category ||
                      productDetails.category ||
                      "Uncategorized",
                    size: productDetails.size || null,
                    images: productDetails.image_link || [],
                    weight: productDetails.weight,
                    sku: productDetails.sku,
                    quantity: item.quantity || 1,
                    orderPrice:
                      item.orderPrice ||
                      item.price ||
                      productDetails.price ||
                      0,
                    productId: productId,
                    fetchSuccess: true,
                  };
                } else {
                  throw new Error("Product not found in API response");
                }
              } else {
                throw new Error("No product ID available");
              }
            } catch (error) {
              return {
                ...item,
                title: `Product ID: ${
                  item.product || item.productId || "Unknown"
                }`,
                description: `Error: ${error.message}`,
                category: "Error",
                quantity: item.quantity || 1,
                orderPrice: item.orderPrice || item.price || 0,
                productId: item.product || item.productId,
                fetchError: true,
                errorMessage: error.message,
              };
            }
          })
        );
        setSelectedOrder({ ...order, items: itemsWithDetails });
      }
      setLoadingOrderDetails(false);
    } catch (error) {
      console.error("Error loading order details:", error);
      setLoadingOrderDetails(false);
      showAlert("Error loading order details: " + error.message, "danger");
    }
  };

  const handleCloseOrderDetails = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
    setLoadingOrderDetails(false);
  };

  const calculateOrderTotals = (items) => {
    if (!items || !Array.isArray(items))
      return { totalItems: 0, totalValue: 0 };
    const totalItems = items.reduce(
      (sum, item) => sum + (item.quantity || 1),
      0
    );
    const totalValue = items.reduce(
      (sum, item) =>
        sum + (item.orderPrice || item.price || 0) * (item.quantity || 1),
      0
    );
    return { totalItems, totalValue };
  };

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: "", variant: "" }), 5000);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const previews = [];
    files.forEach((file, index) => {
      if (!file.type.startsWith("image/")) {
        showAlert(`File ${file.name} is not an image file`, "warning");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showAlert(`File ${file.name} exceeds 5MB limit`, "warning");
        return;
      }
      validFiles.push(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        previews[index] = {
          file: file,
          preview: e.target.result,
          id: Date.now() + index,
          name: file.name,
        };
        if (previews.filter(Boolean).length === validFiles.length) {
          setImagePreviews((prev) => [...prev, ...previews.filter(Boolean)]);
        }
      };
      reader.readAsDataURL(file);
    });
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeImagePreview = (id) => {
    const previewIndex = imagePreviews.findIndex((img) => img.id === id);
    setImagePreviews((prev) => prev.filter((img) => img.id !== id));
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== previewIndex)
    );
  };

  const uploadImageToSupabase = async (file, fileIndex, isEdit = false) => {
    try {
      if (!supabase) {
        throw new Error(
          "Supabase is not configured. Please check your configuration."
        );
      }
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;
      const filePath = `products/${fileName}`;
      const progressSetter = isEdit ? setEditUploadProgress : setUploadProgress;
      progressSetter((prev) => ({
        ...prev,
        [fileIndex]: { progress: 0, fileName: file.name },
      }));
      const { data, error } = await supabase.storage
        .from("blinq")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });
      if (error) {
        console.error("Supabase upload error:", error);
        throw new Error(`Upload failed: ${error.message}`);
      }
      for (let i = 25; i <= 100; i += 25) {
        progressSetter((prev) => ({
          ...prev,
          [fileIndex]: { progress: i, fileName: file.name },
        }));
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      const { data: publicUrlData } = supabase.storage
        .from("blinq")
        .getPublicUrl(filePath);
      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error("Failed to get public URL for uploaded image");
      }
      return publicUrlData.publicUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      const progressSetter = isEdit ? setEditUploadProgress : setUploadProgress;
      progressSetter((prev) => ({
        ...prev,
        [fileIndex]: { progress: 0, fileName: file.name, error: true },
      }));
      throw error;
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.title.trim()) {
      showAlert("Please enter a product title", "warning");
      return;
    }
    if (!newProduct.price || parseFloat(newProduct.price) <= 0) {
      showAlert("Please enter a valid price", "warning");
      return;
    }
    if (!newProduct.description.trim()) {
      showAlert("Please enter a product description", "warning");
      return;
    }
    if (!newProduct.category.trim()) {
      showAlert("Please select a product category", "warning");
      return;
    }
    if (selectedFiles.length === 0) {
      showAlert("Please select at least one image", "warning");
      return;
    }
    setLoading(true);
    const uploadedUrls = [];
    try {
      if (!supabase) {
        throw new Error(
          "Supabase is not configured. Please check your environment variables."
        );
      }
      showAlert("Uploading images...", "info");
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        try {
          const url = await uploadImageToSupabase(file, i, false);
          uploadedUrls.push(url);
        } catch (uploadError) {
          console.error(`Failed to upload ${file.name}:`, uploadError);
          showAlert(
            `Failed to upload ${file.name}: ${uploadError.message}`,
            "warning"
          );
        }
      }
      if (uploadedUrls.length === 0) {
        throw new Error("No images were uploaded successfully");
      }
      showAlert("Images uploaded successfully! Creating product...", "info");
      const productData = {
        Title: newProduct.title.trim(),
        price: parseFloat(newProduct.price),
        Description: newProduct.description.trim(),
        Category: newProduct.category.trim(),
        size: newProduct.size || "",
        image_link: uploadedUrls,
        quantity: parseInt(newProduct.quantity) || 0,
      };
      await axios.post(
        `${process.env.REACT_APP_API_BASE}product/addProduct`,
        productData
      );
      setNewProduct({
        title: "",
        price: "",
        description: "",
        category: "",
        size: "",
        quantity: 0,
      });
      setSelectedFiles([]);
      setImagePreviews([]);
      setUploadProgress({});
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";
      await fetchProducts();
      showAlert(
        `Product added successfully with ${uploadedUrls.length} images!`,
        "success"
      );
    } catch (error) {
      console.error("Error adding product:", error);
      showAlert("Error adding product: " + error.message, "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const product = products.find((p) => p._id === id);
        await axios.delete(
          `${process.env.REACT_APP_API_BASE}product/deletebyID?id=${id}`
        );
        if (
          supabase &&
          product &&
          product.images &&
          Array.isArray(product.images)
        ) {
          for (const imageUrl of product.images) {
            try {
              const urlParts = imageUrl.split("/");
              const fileName = urlParts[urlParts.length - 1];
              const filePath = `products/${fileName}`;
              await supabase.storage.from("blinq").remove([filePath]);
            } catch (imgError) {
              console.warn("Failed to delete image from storage:", imgError);
            }
          }
        }
        fetchProducts();
        showAlert("Product and images deleted successfully!", "success");
      } catch (error) {
        console.error("Error deleting product:", error);
        showAlert("Error deleting product: " + error.message, "danger");
      }
    }
  };

  const handleToggleHidden = async (productId, currentHiddenStatus) => {
    try {
      const product = products.find((p) => p._id === productId);
      if (!product) return;

      const updatedData = {
        Title: product.title || product.Title,
        price: product.price,
        Description: product.description || product.Description,
        Category: product.category || product.Category,
        size: product.size || "",
        image_link: product.image_link || product.images || [],
        quantity: product.quantity || 0,
        isHidden: !currentHiddenStatus,
      };

      await axios.put(
        `${process.env.REACT_APP_API_BASE}product/edit?id=${productId}`,
        updatedData
      );

      await fetchProducts();
      showAlert(
        `Product ${!currentHiddenStatus ? "hidden" : "unhidden"} successfully!`,
        "success"
      );
    } catch (error) {
      console.error("Error toggling product visibility:", error);
      showAlert(
        "Error updating product visibility: " + error.message,
        "danger"
      );
    }
  };

  const clearForm = () => {
    setNewProduct({
      title: "",
      price: "",
      description: "",
      category: "",
      size: "",
      quantity: 0,
    });
    setSelectedFiles([]);
    setImagePreviews([]);
    setUploadProgress({});
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = "";
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditProductData({
      title: product.title || product.Title || "",
      price: product.price || "",
      description: product.description || product.Description || "",
      category: product.category || product.Category || "",
      size: product.size || "",
      images: Array.isArray(product.images)
        ? product.images
        : Array.isArray(product.image_link)
        ? product.image_link
        : [],
      quantity: product.quantity || 0,
      isHidden: product.isHidden || false,
    });
    setEditNewFiles([]);
    setEditImagePreviews([]);
    setEditUploadProgress({});
    setShowEditModal(true);
  };

  const handleEditFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const previews = [];
    files.forEach((file, index) => {
      if (!file.type.startsWith("image/")) {
        showAlert(`File ${file.name} is not an image file`, "warning");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showAlert(`File ${file.name} exceeds 5MB limit`, "warning");
        return;
      }
      validFiles.push(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        previews[index] = {
          file: file,
          preview: e.target.result,
          id: Date.now() + index,
          name: file.name,
          isNew: true,
        };
        if (previews.filter(Boolean).length === validFiles.length) {
          setEditImagePreviews((prev) => [
            ...prev,
            ...previews.filter(Boolean),
          ]);
        }
      };
      reader.readAsDataURL(file);
    });
    setEditNewFiles((prev) => [...prev, ...validFiles]);
  };

  const removeEditImagePreview = (id) => {
    const previewIndex = editImagePreviews.findIndex((img) => img.id === id);
    setEditImagePreviews((prev) => prev.filter((img) => img.id !== id));
    setEditNewFiles((prev) =>
      prev.filter((_, index) => index !== previewIndex)
    );
  };

  const removeExistingImage = (imageUrl) => {
    setEditProductData((prev) => ({
      ...prev,
      images: prev.images.filter((img) => img !== imageUrl),
    }));
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editProductData.title.trim()) {
      showAlert("Please enter a product title", "warning");
      return;
    }
    if (!editProductData.price || parseFloat(editProductData.price) <= 0) {
      showAlert("Please enter a valid price", "warning");
      return;
    }
    if (!editProductData.description.trim()) {
      showAlert("Please enter a product description", "warning");
      return;
    }
    if (!editProductData.category.trim()) {
      showAlert("Please select a product category", "warning");
      return;
    }
    setUpdatingProduct(true);
    const uploadedUrls = [];
    try {
      if (editNewFiles.length > 0) {
        showAlert("Uploading new images...", "info");
        for (let i = 0; i < editNewFiles.length; i++) {
          const file = editNewFiles[i];
          try {
            const url = await uploadImageToSupabase(file, i, true);
            uploadedUrls.push(url);
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError);
            showAlert(
              `Failed to upload ${file.name}: ${uploadError.message}`,
              "warning"
            );
          }
        }
      }
      const allImages = [...editProductData.images, ...uploadedUrls];
      if (allImages.length === 0) {
        showAlert("Product must have at least one image", "warning");
        setUpdatingProduct(false);
        return;
      }
      showAlert("Updating product...", "info");
      const productData = {
        Title: editProductData.title.trim(),
        price: parseFloat(editProductData.price),
        Description: editProductData.description.trim(),
        Category: editProductData.category.trim(),
        size: editProductData.size || "",
        image_link: allImages,
        quantity: parseInt(editProductData.quantity) || 0,
        isHidden: editProductData.isHidden || false,
      };
      await axios.put(
        `${process.env.REACT_APP_API_BASE}product/edit?id=${editingProduct._id}`,
        productData
      );
      setShowEditModal(false);
      setEditingProduct(null);
      setEditProductData({
        title: "",
        price: "",
        description: "",
        category: "",
        size: "",
        images: [],
        quantity: 0,
        isHidden: false,
      });
      setEditNewFiles([]);
      setEditImagePreviews([]);
      setEditUploadProgress({});
      await fetchProducts();
      showAlert("Product updated successfully!", "success");
    } catch (error) {
      console.error("Error updating product:", error);
      showAlert("Error updating product: " + error.message, "danger");
    } finally {
      setUpdatingProduct(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    setEditProductData({
      title: "",
      price: "",
      description: "",
      category: "",
      size: "",
      images: [],
      quantity: 0,
      isHidden: false,
    });
    setEditNewFiles([]);
    setEditImagePreviews([]);
    setEditUploadProgress({});
  };

  const orderCounts = getOrderCounts();

  const getStockBadgeVariant = (quantity) => {
    if (quantity === 0) return "danger";
    if (quantity <= 10) return "warning";
    return "success";
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Show dashboard if authenticated
  return (
    <section style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <Container fluid className="py-4">
        {alert.show && (
          <Row className="mb-3">
            <Col>
              <Alert
                variant={alert.variant}
                dismissible
                onClose={() =>
                  setAlert({ show: false, message: "", variant: "" })
                }
              >
                {alert.message}
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col md={3}>
            <Card className="rounded-4 border border-tertiary mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="fw-semibold mb-0">Admin Panel</h4>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleLogout}
                    title="Logout"
                  >
                    🚪
                  </Button>
                </div>
                <Button
                  variant={activeTab === "products" ? "dark" : "outline-dark"}
                  className="w-100 mb-2"
                  onClick={() => setActiveTab("products")}
                >
                  Manage Products
                </Button>
                <Button
                  variant={activeTab === "orders" ? "dark" : "outline-dark"}
                  className="w-100"
                  onClick={() => setActiveTab("orders")}
                >
                  View Orders
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col md={9}>
            {activeTab === "products" && (
              <Card className="rounded-4 border border-tertiary">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="fw-semibold mb-0">
                      Products ({getFilteredProducts().length})
                    </h3>
                    <div className="d-flex gap-2 align-items-center">
                      <InputGroup style={{ width: "300px" }}>
                        <InputGroup.Text>🔍</InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search products..."
                          value={productSearchTerm}
                          onChange={(e) => setProductSearchTerm(e.target.value)}
                        />
                        {productSearchTerm && (
                          <Button
                            variant="outline-secondary"
                            onClick={() => setProductSearchTerm("")}
                          >
                            ×
                          </Button>
                        )}
                      </InputGroup>
                      <Button
                        variant="outline-secondary"
                        onClick={clearForm}
                        size="sm"
                        disabled={loading}
                      >
                        Clear Form
                      </Button>
                    </div>
                  </div>

                  <Card className="mb-4 bg-light">
                    <Card.Body>
                      <h5 className="mb-3">Add New Product</h5>
                      <Form onSubmit={handleAddProduct}>
                        <Row className="mb-3">
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-medium">
                                Product Title *
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter product title"
                                value={newProduct.title}
                                onChange={(e) =>
                                  setNewProduct({
                                    ...newProduct,
                                    title: e.target.value,
                                  })
                                }
                                disabled={loading}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-medium">
                                Price ($) *
                              </Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={newProduct.price}
                                onChange={(e) =>
                                  setNewProduct({
                                    ...newProduct,
                                    price: e.target.value,
                                  })
                                }
                                required
                                disabled={loading}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-medium">
                                Stock Quantity *
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                placeholder="0"
                                value={newProduct.quantity}
                                onChange={(e) =>
                                  setNewProduct({
                                    ...newProduct,
                                    quantity: e.target.value,
                                  })
                                }
                                required
                                disabled={loading}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-medium">
                                Category *
                              </Form.Label>
                              <Form.Select
                                value={newProduct.category}
                                onChange={(e) =>
                                  setNewProduct({
                                    ...newProduct,
                                    category: e.target.value,
                                  })
                                }
                                required
                                disabled={loading}
                              >
                                <option value="">Select Category</option>
                                <option value="Women's Clothes">
                                  Women's Clothes
                                </option>
                                <option value="Women's Shoes">
                                  Women's Shoes
                                </option>
                                <option value="Women's Bags">
                                  Women's Bags
                                </option>
                                <option value="Women's Accessories">
                                  Women's Accessories
                                </option>
                                <option value="Men's Clothes">
                                  Men's Clothes
                                </option>
                                <option value="Men's Shoes">Men's Shoes</option>
                                <option value="Men's Bags">Men's Bags</option>
                                <option value="Kids' Clothes">
                                  Kids' Clothes
                                </option>
                                <option value="Kids' Shoes">Kids' Shoes</option>
                                <option value="Eyewear">Eyewear</option>
                                <option value="Tools">Tools</option>
                                <option value="Home Appliances">
                                  Home Appliances
                                </option>
                                <option value="Kitchen Appliances">
                                  Kitchen Appliances
                                </option>
                                <option value="Women">Beauty Care Women</option>
                                <option value="Men">Beauty Care Men</option>
                                <option value="Computer Accessories">
                                  Computer Accessories
                                </option>
                                <option value="Baby">Baby</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-medium">
                                Size
                              </Form.Label>
                              <Select
                                isMulti
                                options={sizeOptions}
                                value={sizeOptions.filter((option) =>
                                  (newProduct.size || []).includes(option.value)
                                )}
                                onChange={(selected) =>
                                  setNewProduct({
                                    ...newProduct,
                                    size: selected.map(
                                      (option) => option.value
                                    ),
                                  })
                                }
                                isDisabled={loading}
                                placeholder="Select sizes..."
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-medium">
                                Description *
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Enter product description"
                                value={newProduct.description}
                                onChange={(e) =>
                                  setNewProduct({
                                    ...newProduct,
                                    description: e.target.value,
                                  })
                                }
                                required
                                disabled={loading}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-4">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-medium">
                                Product Images *
                              </Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileSelect}
                                className="mb-2"
                                disabled={loading}
                              />
                              <Form.Text className="text-muted">
                                Select multiple images. Supported: JPG, PNG,
                                GIF. Max 5MB each.
                              </Form.Text>
                              {selectedFiles.length > 0 && (
                                <Badge bg="primary" className="mt-2">
                                  {selectedFiles.length} image(s) selected
                                </Badge>
                              )}
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            {imagePreviews.length > 0 && (
                              <div>
                                <Form.Label className="fw-medium">
                                  Selected Images Preview
                                </Form.Label>
                                <div
                                  className="border rounded p-2"
                                  style={{
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Row>
                                    {imagePreviews.map((img) => (
                                      <Col xs={6} key={img.id} className="mb-2">
                                        <div className="position-relative">
                                          <Image
                                            src={img.preview}
                                            alt={img.name}
                                            style={{
                                              width: "100%",
                                              height: "80px",
                                              objectFit: "cover",
                                            }}
                                            className="rounded"
                                          />
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-1"
                                            style={{
                                              padding: "2px 6px",
                                              fontSize: "12px",
                                            }}
                                            onClick={() =>
                                              removeImagePreview(img.id)
                                            }
                                            disabled={loading}
                                          >
                                            ×
                                          </Button>
                                        </div>
                                        <small className="text-muted d-block text-truncate">
                                          {img.name}
                                        </small>
                                      </Col>
                                    ))}
                                  </Row>
                                </div>
                              </div>
                            )}
                          </Col>
                        </Row>

                        {loading && Object.keys(uploadProgress).length > 0 && (
                          <Row className="mb-3">
                            <Col>
                              <div className="border rounded p-3 bg-white">
                                <h6 className="mb-3">Upload Progress:</h6>
                                {Object.entries(uploadProgress).map(
                                  ([fileIndex, info]) => (
                                    <div key={fileIndex} className="mb-3">
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small className="text-muted">
                                          {info.fileName}
                                        </small>
                                        <div className="d-flex align-items-center">
                                          {info.error ? (
                                            <Badge bg="danger">Failed</Badge>
                                          ) : info.progress === 100 ? (
                                            <Badge bg="success">Complete</Badge>
                                          ) : (
                                            <small>
                                              {Math.round(info.progress)}%
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                      <ProgressBar
                                        now={info.progress}
                                        variant={
                                          info.error
                                            ? "danger"
                                            : info.progress === 100
                                            ? "success"
                                            : "primary"
                                        }
                                        size="sm"
                                        striped={
                                          info.progress < 100 && !info.error
                                        }
                                        animated={
                                          info.progress < 100 && !info.error
                                        }
                                      />
                                    </div>
                                  )
                                )}
                              </div>
                            </Col>
                          </Row>
                        )}

                        <div className="d-flex gap-2">
                          <Button
                            type="submit"
                            variant="dark"
                            disabled={
                              loading ||
                              !newProduct.title ||
                              !newProduct.price ||
                              !newProduct.description ||
                              !newProduct.category ||
                              selectedFiles.length === 0
                            }
                            className="px-4"
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Adding Product...
                              </>
                            ) : (
                              "Add Product"
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={clearForm}
                            disabled={loading}
                          >
                            Clear
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>

                  {selectedProducts.length > 0 && (
                    <Alert variant="info" className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{selectedProducts.length}</strong> product(s)
                          selected
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleMassHide(true)}
                            disabled={loading}
                          >
                            Hide Selected
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleMassHide(false)}
                            disabled={loading}
                          >
                            Unhide Selected
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={handleMassDelete}
                            disabled={loading}
                          >
                            Delete Selected
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedProducts([]);
                              setIsSelectAllChecked(false);
                            }}
                            disabled={loading}
                          >
                            Clear Selection
                          </Button>
                        </div>
                      </div>
                    </Alert>
                  )}

                  <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <Table bordered hover responsive>
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th style={{ width: "50px" }}>
                            <Form.Check
                              type="checkbox"
                              checked={isSelectAllChecked}
                              onChange={handleSelectAll}
                              disabled={
                                loading || getFilteredProducts().length === 0
                              }
                              title="Select all products"
                            />
                          </th>
                          <th>Images</th>
                          <th>Title</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th>Category</th>
                          <th>Size</th>
                          <th>Hidden</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredProducts().length > 0 ? (
                          getFilteredProducts().map((p) => (
                            <tr key={p._id}>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedProducts.includes(p._id)}
                                  onChange={() => handleSelectProduct(p._id)}
                                  disabled={loading}
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-1">
                                  <img
                                    src={
                                      p.image_link && p.image_link.length > 0
                                        ? p.image_link[0]
                                        : "https://via.placeholder.com/40"
                                    }
                                    width={40}
                                    height={40}
                                    style={{
                                      borderRadius: "8px",
                                      objectFit: "cover",
                                    }}
                                    alt="product"
                                    onError={(e) => {
                                      if (!e.target.dataset.failed) {
                                        e.target.src =
                                          "https://via.placeholder.com/40";
                                        e.target.dataset.failed = "true";
                                      }
                                    }}
                                  />
                                </div>
                              </td>
                              <td>{p.title || p.Title}</td>
                              <td>${p.price}</td>
                              <td>
                                <Badge
                                  bg={getStockBadgeVariant(p.quantity || 0)}
                                >
                                  {p.quantity || 0} in stock
                                </Badge>
                              </td>
                              <td>
                                <Badge bg="secondary">
                                  {p.category || p.Category || "No Category"}
                                </Badge>
                              </td>
                              <td>
                                {p.size && p.size.length > 0 ? (
                                  p.size.map((sz, index) => (
                                    <Badge
                                      key={index}
                                      bg="info"
                                      className="me-1"
                                    >
                                      {sz}
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="checkbox"
                                  checked={p.isHidden || false}
                                  onChange={() =>
                                    handleToggleHidden(p._id, p.isHidden)
                                  }
                                  title={
                                    p.isHidden
                                      ? "Product is hidden from customers"
                                      : "Product is visible to customers"
                                  }
                                  style={{ cursor: "pointer" }}
                                />
                                {p.isHidden && (
                                  <Badge
                                    bg="warning"
                                    className="d-block mt-1"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Hidden
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleEditProduct(p)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteProduct(p._id)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="9"
                              className="text-center text-muted py-4"
                            >
                              {productSearchTerm
                                ? `No products found matching "${productSearchTerm}"`
                                : "No products found"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {activeTab === "orders" && (
              <Card className="rounded-4 border border-tertiary">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <h3 className="fw-semibold mb-0">
                        {showCompletedOrders
                          ? "Completed Orders"
                          : "Active Orders"}{" "}
                        ({getFilteredOrdersList().length})
                      </h3>
                      <div className="d-flex gap-2">
                        <Badge bg="primary">Active: {orderCounts.active}</Badge>
                        <Badge bg="secondary">
                          Completed: {orderCounts.completed}
                        </Badge>
                      </div>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <InputGroup style={{ width: "300px" }}>
                        <InputGroup.Text>🔍</InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search orders..."
                          value={orderSearchTerm}
                          onChange={(e) => setOrderSearchTerm(e.target.value)}
                        />
                        {orderSearchTerm && (
                          <Button
                            variant="outline-secondary"
                            onClick={() => setOrderSearchTerm("")}
                          >
                            ×
                          </Button>
                        )}
                      </InputGroup>
                      <Button
                        variant={
                          showCompletedOrders ? "outline-primary" : "primary"
                        }
                        size="sm"
                        onClick={() =>
                          setShowCompletedOrders(!showCompletedOrders)
                        }
                        className="d-flex align-items-center gap-2"
                      >
                        {showCompletedOrders ? (
                          <span>Show Active Orders</span>
                        ) : (
                          <span>Show Completed Orders</span>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <Table bordered hover responsive>
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Phone</th>
                          <th>City</th>
                          <th>Total Price</th>
                          <th>Status</th>
                          <th>Items</th>
                          <th>Address</th>
                          {!showCompletedOrders && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredOrdersList().length > 0 ? (
                          getFilteredOrdersList().map((o) => {
                            const statusInfo = getNextStatusInfo(o.status);
                            return (
                              <tr
                                key={o._id}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOrderClick(o)}
                                className="order-row"
                              >
                                <td>
                                  <small style={{ fontFamily: "monospace" }}>
                                    {o.orderId
                                      ? `#${o.orderId}`
                                      : `${o._id.substring(0, 8)}...`}
                                  </small>
                                </td>
                                <td>
                                  {o.shippingAddress?.fullName ||
                                    o.shippingAddress?.name}
                                </td>
                                <td>{o.shippingAddress?.phone}</td>
                                <td>{o.shippingAddress?.city}</td>
                                <td>${o.totalPrice}</td>
                                <td>
                                  <Badge bg={getStatusButtonVariant(o.status)}>
                                    {o.status || "pending"}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge bg="info">
                                    {o.items?.length} items
                                  </Badge>
                                </td>
                                <td>
                                  <div style={{ maxWidth: "200px" }}>
                                    {o.shippingAddress?.address?.substring(
                                      0,
                                      50
                                    )}
                                    {o.shippingAddress?.address?.length > 50 &&
                                      "..."}
                                  </div>
                                </td>
                                {!showCompletedOrders && (
                                  <td onClick={(e) => e.stopPropagation()}>
                                    {statusInfo.nextStatus ? (
                                      <Button
                                        variant={getStatusButtonVariant(
                                          statusInfo.nextStatus
                                        )}
                                        size="sm"
                                        onClick={(e) =>
                                          updateOrderStatus(
                                            o._id,
                                            statusInfo.nextStatus,
                                            e
                                          )
                                        }
                                        disabled={updatingOrderStatus}
                                        className="d-flex align-items-center gap-1"
                                        style={{ fontSize: "12px" }}
                                      >
                                        {updatingOrderStatus ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <span>{statusInfo.buttonText}</span>
                                        )}
                                      </Button>
                                    ) : (
                                      <Button
                                        variant={getStatusButtonVariant(
                                          o.status
                                        )}
                                        size="sm"
                                        disabled
                                        style={{ fontSize: "12px" }}
                                      >
                                        <span>{statusInfo.buttonText}</span>
                                      </Button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={showCompletedOrders ? "8" : "9"}
                              className="text-center text-muted py-4"
                            >
                              {orderSearchTerm
                                ? `No orders found matching "${orderSearchTerm}"`
                                : showCompletedOrders
                                ? "No completed orders found"
                                : "No active orders found"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        <Modal
          show={showEditModal}
          onHide={handleCloseEditModal}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Product</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {editingProduct && (
              <Form onSubmit={handleUpdateProduct}>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">
                        Product Title *
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter product title"
                        value={editProductData.title}
                        onChange={(e) =>
                          setEditProductData({
                            ...editProductData,
                            title: e.target.value,
                          })
                        }
                        disabled={updatingProduct}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Price ($) *</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={editProductData.price}
                        onChange={(e) =>
                          setEditProductData({
                            ...editProductData,
                            price: e.target.value,
                          })
                        }
                        disabled={updatingProduct}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">
                        Stock Quantity *
                      </Form.Label>
                      <InputGroup>
                        <Button
                          variant="outline-secondary"
                          onClick={() =>
                            setEditProductData({
                              ...editProductData,
                              quantity: Math.max(
                                0,
                                (editProductData.quantity || 0) - 1
                              ),
                            })
                          }
                          disabled={
                            updatingProduct || editProductData.quantity <= 0
                          }
                        >
                          -
                        </Button>
                        <Form.Control
                          type="number"
                          min="0"
                          placeholder="0"
                          value={editProductData.quantity}
                          onChange={(e) =>
                            setEditProductData({
                              ...editProductData,
                              quantity: Math.max(
                                0,
                                parseInt(e.target.value) || 0
                              ),
                            })
                          }
                          disabled={updatingProduct}
                          className="text-center"
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() =>
                            setEditProductData({
                              ...editProductData,
                              quantity: (editProductData.quantity || 0) + 1,
                            })
                          }
                          disabled={updatingProduct}
                        >
                          +
                        </Button>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Current:{" "}
                        <Badge
                          bg={getStockBadgeVariant(editProductData.quantity)}
                        >
                          {editProductData.quantity}
                        </Badge>
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Size</Form.Label>
                      <Select
                        isMulti
                        options={sizeOptions}
                        value={sizeOptions.filter((option) =>
                          (editProductData.size || []).includes(option.value)
                        )}
                        onChange={(selected) =>
                          setEditProductData({
                            ...editProductData,
                            size: selected.map((option) => option.value),
                          })
                        }
                        isDisabled={updatingProduct}
                        placeholder="Select sizes..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Category *</Form.Label>
                      <Form.Select
                        value={editProductData.category}
                        onChange={(e) =>
                          setEditProductData({
                            ...editProductData,
                            category: e.target.value,
                          })
                        }
                        disabled={updatingProduct}
                      >
                        <option value="">Select Category</option>
                        <option value="Women's Clothes">Women's Clothes</option>
                        <option value="Women's Shoes">Women's Shoes</option>
                        <option value="Women's Bags">Women's Bags</option>
                        <option value="Women's Accessories">
                          Women's Accessories
                        </option>
                        <option value="Men's Clothes">Men's Clothes</option>
                        <option value="Men's Shoes">Men's Shoes</option>
                        <option value="Men's Bags">Men's Bags</option>
                        <option value="Kids' Clothes">Kids' Clothes</option>
                        <option value="Kids' Shoes">Kids' Shoes</option>
                        <option value="Eyewear">Eyewear</option>
                        <option value="Tools">Tools</option>
                        <option value="Home Appliances">Home Appliances</option>
                        <option value="Kitchen Appliances">
                          Kitchen Appliances
                        </option>
                        <option value="Women">Beauty Care Women</option>
                        <option value="Men">Beauty Care Men</option>
                        <option value="Computer Accessories">
                          Computer Accessories
                        </option>
                        <option value="Baby">Baby</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">
                        Description *
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter product description"
                        value={editProductData.description}
                        onChange={(e) =>
                          setEditProductData({
                            ...editProductData,
                            description: e.target.value,
                          })
                        }
                        disabled={updatingProduct}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="isHiddenCheckbox"
                        label={
                          <span>
                            <strong>Hide this product from customers</strong>
                            <br />
                            <small className="text-muted">
                              When checked, this product will not be visible in
                              the store
                            </small>
                          </span>
                        }
                        checked={editProductData.isHidden}
                        onChange={(e) =>
                          setEditProductData({
                            ...editProductData,
                            isHidden: e.target.checked,
                          })
                        }
                        disabled={updatingProduct}
                      />
                      {editProductData.isHidden && (
                        <Badge bg="warning" className="mt-2">
                          ⚠ Product is currently hidden from customers
                        </Badge>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {editProductData.images.length > 0 && (
                  <div className="mb-4">
                    <Form.Label className="fw-medium">
                      Existing Images ({editProductData.images.length})
                    </Form.Label>
                    <div
                      className="border rounded p-3"
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      <Row>
                        {editProductData.images.map((imgUrl, idx) => (
                          <Col xs={6} md={3} key={idx} className="mb-3">
                            <div className="position-relative">
                              <Image
                                src={imgUrl}
                                alt={`Product ${idx + 1}`}
                                style={{
                                  width: "100%",
                                  height: "120px",
                                  objectFit: "cover",
                                }}
                                className="rounded"
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/120";
                                }}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="position-absolute top-0 end-0 m-1"
                                style={{ padding: "4px 8px", fontSize: "14px" }}
                                onClick={() => removeExistingImage(imgUrl)}
                                disabled={updatingProduct}
                              >
                                ×
                              </Button>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </div>
                )}

                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">
                        Add New Images
                      </Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleEditFileSelect}
                        disabled={updatingProduct}
                      />
                      <Form.Text className="text-muted">
                        Select additional images. Max 5MB each.
                      </Form.Text>
                      {editNewFiles.length > 0 && (
                        <Badge bg="primary" className="mt-2">
                          {editNewFiles.length} new image(s) selected
                        </Badge>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    {editImagePreviews.length > 0 && (
                      <div>
                        <Form.Label className="fw-medium">
                          New Images Preview
                        </Form.Label>
                        <div
                          className="border rounded p-2"
                          style={{ maxHeight: "200px", overflowY: "auto" }}
                        >
                          <Row>
                            {editImagePreviews.map((img) => (
                              <Col xs={6} key={img.id} className="mb-2">
                                <div className="position-relative">
                                  <Image
                                    src={img.preview}
                                    alt={img.name}
                                    style={{
                                      width: "100%",
                                      height: "80px",
                                      objectFit: "cover",
                                    }}
                                    className="rounded"
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="position-absolute top-0 end-0 m-1"
                                    style={{
                                      padding: "2px 6px",
                                      fontSize: "12px",
                                    }}
                                    onClick={() =>
                                      removeEditImagePreview(img.id)
                                    }
                                    disabled={updatingProduct}
                                  >
                                    ×
                                  </Button>
                                </div>
                                <small className="text-muted d-block text-truncate">
                                  {img.name}
                                </small>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>

                {updatingProduct &&
                  Object.keys(editUploadProgress).length > 0 && (
                    <Row className="mb-3">
                      <Col>
                        <div className="border rounded p-3 bg-white">
                          <h6 className="mb-3">Upload Progress:</h6>
                          {Object.entries(editUploadProgress).map(
                            ([fileIndex, info]) => (
                              <div key={fileIndex} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <small className="text-muted">
                                    {info.fileName}
                                  </small>
                                  <div className="d-flex align-items-center">
                                    {info.error ? (
                                      <Badge bg="danger">Failed</Badge>
                                    ) : info.progress === 100 ? (
                                      <Badge bg="success">Complete</Badge>
                                    ) : (
                                      <small>
                                        {Math.round(info.progress)}%
                                      </small>
                                    )}
                                  </div>
                                </div>
                                <ProgressBar
                                  now={info.progress}
                                  variant={
                                    info.error
                                      ? "danger"
                                      : info.progress === 100
                                      ? "success"
                                      : "primary"
                                  }
                                  size="sm"
                                  striped={info.progress < 100 && !info.error}
                                  animated={info.progress < 100 && !info.error}
                                />
                              </div>
                            )
                          )}
                        </div>
                      </Col>
                    </Row>
                  )}
              </Form>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseEditModal}
              disabled={updatingProduct}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateProduct}
              disabled={
                updatingProduct ||
                !editProductData.title ||
                !editProductData.price ||
                !editProductData.description ||
                !editProductData.category ||
                (editProductData.images.length === 0 &&
                  editNewFiles.length === 0)
              }
            >
              {updatingProduct ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                "Update Product"
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showOrderDetails}
          onHide={handleCloseOrderDetails}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center gap-3">
                <span>Order Details</span>
                {selectedOrder && (
                  <>
                    <Badge bg="secondary" style={{ fontFamily: "monospace" }}>
                      {selectedOrder.orderId}
                    </Badge>
                    <Badge bg={getStatusButtonVariant(selectedOrder.status)}>
                      {selectedOrder.status || "pending"}
                    </Badge>
                  </>
                )}
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {loadingOrderDetails ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3 text-muted">Loading order details...</p>
              </div>
            ) : selectedOrder ? (
              <div>
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Customer Information</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong>Name:</strong>{" "}
                          {selectedOrder.shippingAddress?.fullName ||
                            selectedOrder.shippingAddress?.name ||
                            "N/A"}
                        </div>
                        <div className="mb-3">
                          <strong>Phone:</strong>{" "}
                          {selectedOrder.shippingAddress?.phone || "N/A"}
                        </div>
                        <div className="mb-3">
                          <strong>City:</strong>{" "}
                          {selectedOrder.shippingAddress?.city || "N/A"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong>Address:</strong>
                          <br />
                          <span className="text-muted">
                            {selectedOrder.shippingAddress?.address || "N/A"}
                          </span>
                        </div>
                        <div className="mb-3">
                          <strong>Coordinates:</strong>
                          <br />
                          <small className="text-muted">
                            Lat:{" "}
                            {selectedOrder.shippingAddress?.latitude || "N/A"},
                            Lng:{" "}
                            {selectedOrder.shippingAddress?.longitude || "N/A"}
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Order Summary</h5>
                    <div className="d-flex gap-2">
                      {(() => {
                        const totals = calculateOrderTotals(
                          selectedOrder.items
                        );
                        return (
                          <>
                            <Badge bg="info">{totals.totalItems} Items</Badge>
                            <Badge bg="success">
                              ${selectedOrder.totalPrice}
                            </Badge>
                          </>
                        );
                      })()}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <strong>Order ID:</strong>
                      <span
                        className="ms-2"
                        style={{ fontFamily: "monospace" }}
                      >
                        {selectedOrder.orderId}
                      </span>
                    </div>
                    <div className="mb-3">
                      <strong>Order Date:</strong>
                      <span className="ms-2">
                        {selectedOrder.createdAt
                          ? new Date(
                              selectedOrder.createdAt
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "N/A"}
                      </span>
                    </div>
                    <div className="mb-3">
                      <strong>Status:</strong>
                      <Badge
                        bg={getStatusButtonVariant(selectedOrder.status)}
                        className="ms-2"
                      >
                        {selectedOrder.status || "Pending"}
                      </Badge>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Ordered Items</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <Table striped hover className="mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Product</th>
                            <th>Price</th>
                            <th>Quantity</th>
                            <th>Size</th>
                            <th>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.items &&
                          selectedOrder.items.length > 0 ? (
                            selectedOrder.items.map((item, index) => (
                              <tr key={index}>
                                <td>
                                  <div>
                                    <strong>
                                      {item.title ||
                                        item.Title ||
                                        item.name ||
                                        item.productName ||
                                        `Product ID: ${item.product}` ||
                                        "Unknown Product"}
                                    </strong>
                                    {item.fetchError && (
                                      <div className="text-danger small">
                                        <small>
                                          ⚠ Product details unavailable
                                        </small>
                                      </div>
                                    )}
                                    {(item.description || item.Description) && (
                                      <div className="text-muted small">
                                        {(
                                          item.description || item.Description
                                        ).substring(0, 100)}
                                        {(item.description || item.Description)
                                          .length > 100 && "..."}
                                      </div>
                                    )}
                                    {(item.category || item.Category) && (
                                      <Badge
                                        bg="outline-secondary"
                                        className="mt-1"
                                      >
                                        {item.category || item.Category}
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <strong>
                                    ${item.orderPrice || item.price || 0}
                                  </strong>
                                </td>
                                <td>
                                  <Badge bg="primary">
                                    {item.quantity || 1}
                                  </Badge>
                                </td>
                                <td>
                                  {(item.size
                                    ? Array.isArray(item.size)
                                      ? item.size
                                      : [item.size]
                                    : []
                                  ).length > 0 ? (
                                    (Array.isArray(item.size)
                                      ? item.size
                                      : [item.size]
                                    ).map((sz, index) => (
                                      <Badge
                                        key={index}
                                        bg="info"
                                        className="me-1"
                                      >
                                        {sz}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>

                                <td>
                                  <strong>
                                    $
                                    {(
                                      (item.orderPrice || item.price || 0) *
                                      (item.quantity || 1)
                                    ).toFixed(2)}
                                  </strong>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="5"
                                className="text-center text-muted py-4"
                              >
                                No items found in this order
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {selectedOrder.items && selectedOrder.items.length > 0 && (
                      <div className="border-top p-3 bg-light">
                        <Row>
                          <Col md={8}>
                            <div className="d-flex justify-content-end">
                              <div>
                                {(() => {
                                  const totals = calculateOrderTotals(
                                    selectedOrder.items
                                  );
                                  return (
                                    <div>
                                      <div className="mb-2">
                                        <strong>Total Items: </strong>
                                        <span>{totals.totalItems}</span>
                                      </div>
                                      <div className="mb-2">
                                        <strong>Subtotal: </strong>
                                        <span>
                                          ${totals.totalValue.toFixed(2)}
                                        </span>
                                      </div>
                                      <div className="border-top pt-2">
                                        <strong>Order Total: </strong>
                                        <span className="text-success fs-5">
                                          ${selectedOrder.totalPrice}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>
            ) : (
              <div className="text-center text-muted py-5">
                No order selected
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseOrderDetails}>
              Close
            </Button>
            {selectedOrder && (
              <div className="d-flex gap-2">
                {!showCompletedOrders &&
                  selectedOrder.status?.toLowerCase() !== "canceled" &&
                  selectedOrder.status?.toLowerCase() !== "cancelled" &&
                  selectedOrder.status?.toLowerCase() !== "delivered" && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => cancelOrder(selectedOrder._id)}
                      disabled={updatingOrderStatus}
                    >
                      {updatingOrderStatus ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          Cancelling...
                        </>
                      ) : (
                        "Cancel Order"
                      )}
                    </Button>
                  )}

                {!showCompletedOrders &&
                  (() => {
                    const statusInfo = getNextStatusInfo(selectedOrder.status);
                    if (statusInfo.nextStatus) {
                      return (
                        <Button
                          variant={getStatusButtonVariant(
                            statusInfo.nextStatus
                          )}
                          size="sm"
                          onClick={() =>
                            updateOrderStatus(
                              selectedOrder._id,
                              statusInfo.nextStatus
                            )
                          }
                          disabled={updatingOrderStatus}
                          className="d-flex align-items-center gap-1"
                        >
                          {updatingOrderStatus ? (
                            <>
                              <Spinner size="sm" />
                              <span>Updating...</span>
                            </>
                          ) : (
                            <span>{statusInfo.buttonText}</span>
                          )}
                        </Button>
                      );
                    }
                    return null;
                  })()}
              </div>
            )}
          </Modal.Footer>
        </Modal>

        <style>{`
          .order-row:hover {
            background-color: #f8f9fa !important;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
          }
        `}</style>
      </Container>
    </section>
  );
};

export default AdminDashboard;
